import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import { Button } from "../../../../Ui_Components/Button";
import { calculateCPFRatioFromStrings, formatCPFRatio } from "../../CPFCalculator";

export interface NutrientValuesFormData {
  totalCaloriesMin: string;
  totalCaloriesMax: string;
  totalProteinMin: string;
  totalProteinMax: string;
  totalCarbsMin: string;
  totalCarbsMax: string;
  totalFatsMin: string;
  totalFatsMax: string;
  totalGLMin: string;
  totalGLMax: string;
}

export interface EditNutrientValuesPayload {
  total_calories_min: number;
  total_calories_max: number;
  total_protein_min: number;
  total_protein_max: number;
  total_carbs_min: number;
  total_carbs_max: number;
  total_fats_min: number;
  total_fats_max: number;
  total_GL_min: number;
  total_GL_max: number;
  CPF_min: string;
  CPF_max: string;
}

interface EditNutrientValuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: EditNutrientValuesPayload) => void;
  initialValues: NutrientValuesFormData;
  isSaving?: boolean;
}

const inputClass = (hasError: boolean) =>
  `w-full px-4 py-2 border ${
    hasError ? "border-red-500" : "border-[#E0E0E0]"
  } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`;

const EditNutrientValuesModal: React.FC<EditNutrientValuesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
  isSaving = false,
}) => {
  const [form, setForm] = useState<NutrientValuesFormData>(initialValues);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initialValues);
      setTouched(false);
    }
  }, [isOpen, initialValues]);

  const cpfMin = useMemo(
    () =>
      calculateCPFRatioFromStrings(
        form.totalCarbsMin,
        form.totalProteinMin,
        form.totalFatsMin,
        form.totalCaloriesMin,
      ),
    [
      form.totalCarbsMin,
      form.totalProteinMin,
      form.totalFatsMin,
      form.totalCaloriesMin,
    ],
  );

  const cpfMax = useMemo(
    () =>
      calculateCPFRatioFromStrings(
        form.totalCarbsMax,
        form.totalProteinMax,
        form.totalFatsMax,
        form.totalCaloriesMax,
      ),
    [
      form.totalCarbsMax,
      form.totalProteinMax,
      form.totalFatsMax,
      form.totalCaloriesMax,
    ],
  );

  const isMinMaxInvalid = (min: string, max: string) => {
    if (!min || !max) return false;
    return parseFloat(min) > parseFloat(max);
  };

  const isFieldEmpty = (v: string) => !v || v.trim() === "";

  const errors = useMemo(
    () => ({
      calories:
        isFieldEmpty(form.totalCaloriesMin) ||
        isFieldEmpty(form.totalCaloriesMax) ||
        isMinMaxInvalid(form.totalCaloriesMin, form.totalCaloriesMax),
      protein:
        isFieldEmpty(form.totalProteinMin) ||
        isFieldEmpty(form.totalProteinMax) ||
        isMinMaxInvalid(form.totalProteinMin, form.totalProteinMax),
      carbs:
        isFieldEmpty(form.totalCarbsMin) ||
        isFieldEmpty(form.totalCarbsMax) ||
        isMinMaxInvalid(form.totalCarbsMin, form.totalCarbsMax),
      fats:
        isFieldEmpty(form.totalFatsMin) ||
        isFieldEmpty(form.totalFatsMax) ||
        isMinMaxInvalid(form.totalFatsMin, form.totalFatsMax),
      gl:
        isFieldEmpty(form.totalGLMin) ||
        isFieldEmpty(form.totalGLMax) ||
        isMinMaxInvalid(form.totalGLMin, form.totalGLMax),
    }),
    [form],
  );

  const isFormValid =
    !errors.calories &&
    !errors.protein &&
    !errors.carbs &&
    !errors.fats &&
    !errors.gl;

  const handleChange = (field: keyof NutrientValuesFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setTouched(true);
    if (!isFormValid || isSaving) return;
    onSave({
      total_calories_min: parseFloat(form.totalCaloriesMin) || 0,
      total_calories_max: parseFloat(form.totalCaloriesMax) || 0,
      total_protein_min: parseFloat(form.totalProteinMin) || 0,
      total_protein_max: parseFloat(form.totalProteinMax) || 0,
      total_carbs_min: parseFloat(form.totalCarbsMin) || 0,
      total_carbs_max: parseFloat(form.totalCarbsMax) || 0,
      total_fats_min: parseFloat(form.totalFatsMin) || 0,
      total_fats_max: parseFloat(form.totalFatsMax) || 0,
      total_GL_min: parseFloat(form.totalGLMin) || 0,
      total_GL_max: parseFloat(form.totalGLMax) || 0,
      CPF_min: formatCPFRatio(cpfMin.ratio),
      CPF_max: formatCPFRatio(cpfMax.ratio),
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg 3xl:text-xl font-semibold font-inter text-[#1F1F1F] mb-5">
          Edit nutrient values
        </h2>

        <div className="grid grid-cols-3 gap-x-6 gap-y-5">
          {/* Total calories */}
          <div>
            <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
              Total calories <span className="text-[#8E8E93] font-normal">(kcals)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={form.totalCaloriesMin}
                onChange={(e) => handleChange("totalCaloriesMin", e.target.value)}
                className={inputClass(touched && errors.calories)}
              />
              <span className="text-[#8E8E93]">to</span>
              <input
                type="number"
                placeholder="Max"
                value={form.totalCaloriesMax}
                onChange={(e) => handleChange("totalCaloriesMax", e.target.value)}
                className={inputClass(touched && errors.calories)}
              />
            </div>
          </div>

          {/* Total Adj.Protein */}
          <div>
            <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
              Total Adj.Protein <span className="text-[#8E8E93] font-normal">(g)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={form.totalProteinMin}
                onChange={(e) => handleChange("totalProteinMin", e.target.value)}
                className={inputClass(touched && errors.protein)}
              />
              <span className="text-[#8E8E93]">to</span>
              <input
                type="number"
                placeholder="Max"
                value={form.totalProteinMax}
                onChange={(e) => handleChange("totalProteinMax", e.target.value)}
                className={inputClass(touched && errors.protein)}
              />
            </div>
          </div>

          {/* Total Net Carbs */}
          <div>
            <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
              Total Net Carbs <span className="text-[#8E8E93] font-normal">(g)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={form.totalCarbsMin}
                onChange={(e) => handleChange("totalCarbsMin", e.target.value)}
                className={inputClass(touched && errors.carbs)}
              />
              <span className="text-[#8E8E93]">to</span>
              <input
                type="number"
                placeholder="Max"
                value={form.totalCarbsMax}
                onChange={(e) => handleChange("totalCarbsMax", e.target.value)}
                className={inputClass(touched && errors.carbs)}
              />
            </div>
          </div>

          {/* Total fats */}
          <div>
            <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
              Total fats <span className="text-[#8E8E93] font-normal">(g)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={form.totalFatsMin}
                onChange={(e) => handleChange("totalFatsMin", e.target.value)}
                className={inputClass(touched && errors.fats)}
              />
              <span className="text-[#8E8E93]">to</span>
              <input
                type="number"
                placeholder="Max"
                value={form.totalFatsMax}
                onChange={(e) => handleChange("totalFatsMax", e.target.value)}
                className={inputClass(touched && errors.fats)}
              />
            </div>
          </div>

          {/* Total GL */}
          <div>
            <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
              Total GL <span className="text-[#8E8E93] font-normal">(g)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={form.totalGLMin}
                onChange={(e) => handleChange("totalGLMin", e.target.value)}
                className={inputClass(touched && errors.gl)}
              />
              <span className="text-[#8E8E93]">to</span>
              <input
                type="number"
                placeholder="Max"
                value={form.totalGLMax}
                onChange={(e) => handleChange("totalGLMax", e.target.value)}
                className={inputClass(touched && errors.gl)}
              />
            </div>
          </div>

          {/* C:P:F ratio (auto-calculated) */}
          <div>
            <label className="text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2 flex items-center justify-between gap-1">
              C:P:F ratio
              <Info className="w-4 h-4 text-[#8E8E93]" />
            </label>
            <div className="flex items-center gap-2">
              <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#8E8E93]">
                {cpfMin.ratio.c} : {cpfMin.ratio.p} : {cpfMin.ratio.f}
              </div>
              <span className="text-[#8E8E93]">to</span>
              <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#8E8E93]">
                {cpfMax.ratio.c} : {cpfMax.ratio.p} : {cpfMax.ratio.f}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving || !isFormValid}
          >
            {isSaving ? "Saving..." : "Save & update"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditNutrientValuesModal;
